/* eslint-disable max-lines-per-function */
/* eslint-disable max-lines */
import { createSlice } from '@reduxjs/toolkit';
import { AssignedForms, assignFormThunk, connectExternalFormThunk, createDefaultFormsThunk, createUserFormThunk, deleteFormThunk, duplicateFormThunk, editUserFormThunk, fetchFacebookPagesThunk, fetchFormsThunk, fetchFormTemplatesThunk, formSettingsThunk, previewRenderFormThunk, refetchAllPagesThunk, removeAssigneeFormThunk } from '../thunks/form';
import { FacebookPagesObject, FormObject, FormState, FormTemplatesObject } from '../../params/redux/form';

// Initial state with default values for authentication and user info
const initialState: FormState = {
    forms: [],
    assignedForms: [],
    selectedForm: null,
    codes: {
        snapps_code: '',
        elementor_code: '',
        script_code: ''
    },
    manual: {
        selectedForm: null,
        selectedFormLeads: [],
        isManual: false,
        headers: [],
        rows: [],
        mapping: {}
    },
    formPages: [],
    formTemplates: [],
    formHTML: '',
    filters: {
        isDeleted: false,
        isExternal: false,
        isInternal: false
    },
    formFields: {
        formTitle: 'Form 1',
        formDescription: '',
        isEditing: false,
        pipelineId: 0,
        stageId: 0,
        formTemplateId: 2,
        redirectMessage: '',
        showTitle: 1,
        redirectUrl: '',
        assignees: [],
        fields: [
            {
                id: `field-submit-${Date.now()}`,
                type: 'submit',
                label: 'Submit',
                fontFamily: 'inherit',
                labelColor: '#fff',
                bgColor: '#000000',
                btnSize: 'small'
            }
        ]
    },
    selectedFormField: null,
    isCreatingFormFlag: false,
    selectedFormFlag: false,
    preview: false,
    settings: false,
    selectedFormType: 'form',
    loaders: {
        isLoading: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isSearching: false,
        isInserting: false
    }
};

// Slice for app-related states and actions
const formSlice = createSlice({
    // Slice name
    name: 'form',
    // inital state for the slice
    initialState,
    reducers: {
        addingFormField: (state, action) => {
            state.formFields.fields.push(action.payload);
        },
        duplicateFormField: (state, action) => {
            const fieldToDuplicate = state.formFields.fields.find((field) => field.id === action.payload);
            if (fieldToDuplicate) {
                const duplicatedField = {
                    ...fieldToDuplicate,
                    id: `field-${fieldToDuplicate.type}-${Date.now()}`,
                    label: `${fieldToDuplicate.label} (Copy)`
                };
                state.formFields.fields.push(duplicatedField);
            }
        },
        setFilterForForms: (state, action) => {
            const { isDeleted, isInternal, isExternal } = action.payload;
            state.filters = {
                isDeleted,
                isInternal,
                isExternal
            };
        },
        editFormField: (state, action) => {
            state.formFields.fields = state.formFields.fields.map((field) => (field.id === state.selectedFormField?.id ? { ...field, ...action.payload } : field));
            state.selectedFormField = { ...state.selectedFormField, ...action.payload };
        },
        removeFormField: (state, action) => {
            state.formFields.fields = state.formFields.fields.filter((field) => field.id !== action.payload);
            if (action.payload === state.selectedFormField?.id) {
                state.selectedFormField = null;
            }
        },
        settingSelectedFormType: (state, action) => {
            state.selectedFormType = action.payload;
        },
        settingFormFieldObject: (state, action) => {
            state.formFields = action.payload;
            state.selectedFormField = null;
        },
        settingSelectedFormFlag: (state, action) => {
            state.selectedFormFlag = action.payload;
        },
        settingFormSettingsFlag: (state, action) => {
            state.settings = action.payload;
        },
        settingSelectedFormField: (state, action) => {
            state.selectedFormField = action.payload;
        },
        settingFormManualMapping: (state, action) => {
            const { selectedForm, isManual, headers, rows, mapping, selectedLeads } = action.payload;
            state.manual.selectedForm = selectedForm;
            state.manual.isManual = isManual;
            state.manual.headers = headers;
            state.manual.rows = rows;
            state.manual.mapping = mapping;
            state.manual.selectedFormLeads = selectedLeads || [];
        },
        reorderFormFields: (state, action) => {
            const { sourceIndex, destinationIndex } = action.payload;
            const submitField = state.formFields.fields.find((f) => f.type === 'submit');
            const nonSubmitFields = state.formFields.fields.filter((f) => f.type !== 'submit');
            if (sourceIndex !== destinationIndex) {
                const [movedField] = nonSubmitFields.splice(sourceIndex, 1);
                nonSubmitFields.splice(destinationIndex, 0, movedField);
            }
            state.formFields.fields = submitField
                ? [...nonSubmitFields, submitField]
                : nonSubmitFields;
            // if (sourceIndex !== destinationIndex) {
            //     const updateFields = [...state.formFields.fields];
            //     const [movedField] = updateFields.splice(sourceIndex, 1);
            //     updateFields.splice(destinationIndex, 0, movedField);
            //     state.formFields.fields = updateFields;
            // }
        },
        settingSelectedForm: (state, action) => {
            state.selectedForm = action.payload;
        },
        editFormTitle: (state, action) => {
            state.formFields.formTitle = action.payload;
        },
        settingCreateFormFlag: (state, action) => {
            state.isCreatingFormFlag = action.payload;
        },
        settingPreviewFlag: (state, action) => {
            state.preview = action.payload;
        },
        // Resets loading states related to app actions
        resettingFormLoader: (state) => {
            state.loaders = initialState.loaders;
        },
        // Resets all app data to the initial state
        resettingFormData: () => initialState
    },
    extraReducers: (builder) => {
        // Handle async actions for login
        builder
            .addCase(createUserFormThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(createUserFormThunk.fulfilled, () => {
                //
            })
            .addCase(createUserFormThunk.rejected, () => {
                //
            });
        builder
            .addCase(fetchFormsThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchFormsThunk.fulfilled, (state, action) => {
                const { forms, assignedForms } = action.payload as {
                    forms: FormObject[];
                    assignedForms: AssignedForms[];
                };
                state.forms = forms.map((form) => ({
                    ...form,
                    readOnly: false
                }));
                state.formFields.assignees = forms.find((form) => form.id === state.selectedForm?.id)?.assignees || [];
                state.assignedForms = [];
                // assignedForms.map((form) => ({
                //     id: form.form.id,
                //     title: form.form.title,
                //     form_json: form.form.form_json,
                //     embed_code: form.form.embed_code,
                //     form_code: form.form.form_code,
                //     user_id: form.form.user_id,
                //     client_id: form.form.client_id,
                //     agency_id: form.form.agency_id,
                //     form_user_id: form.form.form_user_id,
                //     pipeline_id: form.form.pipeline_id,
                //     stage_id: form.form.stage_id,
                //     type: form.form.type,
                //     status: form.form.status,
                //     created_at: form.form.created_at,
                //     updated_at: form.form.updated_at,
                //     submissions: form.form.submissions,
                //     redirect_url: form.form.redirect_url,
                //     redirect_msg: form.form.redirect_msg,
                //     template_id: form.form.template_id,
                //     external_form_id: form.form.external_form_id,
                //     external_id: form.form.external_id,
                //     page_id: form.form.page_id,
                //     mapping_json: form.mapping_json,
                //     assignees: [],
                //     show_title: form.form.show_title || 1,
                //     readOnly: true
                // }));
            })
            .addCase(fetchFormsThunk.rejected, (state) => {
                state.forms = [];
            });
        builder
            .addCase(editUserFormThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(editUserFormThunk.fulfilled, () => {
                //
            })
            .addCase(editUserFormThunk.rejected, () => {
                //
            });
        builder
            .addCase(fetchFacebookPagesThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchFacebookPagesThunk.fulfilled, (state, action) => {
                state.formPages = action.payload as FacebookPagesObject[];
            })
            .addCase(fetchFacebookPagesThunk.rejected, (state) => {
                state.formPages = [];
            });
        builder
            .addCase(deleteFormThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isDeleting = true;
            })
            .addCase(deleteFormThunk.fulfilled, () => {
                //
            })
            .addCase(deleteFormThunk.rejected, () => {
                //
            });
        builder
            .addCase(duplicateFormThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(duplicateFormThunk.fulfilled, () => {
                //
            })
            .addCase(duplicateFormThunk.rejected, () => {
                //
            });
        builder
            .addCase(createDefaultFormsThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(createDefaultFormsThunk.fulfilled, (state, action) => {
                const { snapps_code, elementor_code, custom_code } = action.payload as {
                    snapps_code: string;
                    elementor_code: string;
                    custom_code: string;
                };
                state.codes.snapps_code = `${process.env.REACT_APP_SNAPPS_FORM_LINK}${snapps_code}`;
                state.codes.elementor_code = `${process.env.REACT_APP_ELEMENTOR_FORM_LINK}${elementor_code}`;
                state.codes.script_code = `<script src="${process.env.REACT_APP_CUSTOM_FORM_LINK}" company="${custom_code}"></script>`;
            })
            .addCase(createDefaultFormsThunk.rejected, (state) => {
                state.codes.snapps_code = '';
                state.codes.elementor_code = '';
            });
        builder
            .addCase(assignFormThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(assignFormThunk.fulfilled, () => {
                //
            })
            .addCase(assignFormThunk.rejected, () => {
                //
            });
        builder
            .addCase(removeAssigneeFormThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(removeAssigneeFormThunk.fulfilled, () => {
                //
            })
            .addCase(removeAssigneeFormThunk.rejected, () => {
                //
            });
        builder
            .addCase(formSettingsThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(formSettingsThunk.fulfilled, () => {
                //
            })
            .addCase(formSettingsThunk.rejected, () => {
                //
            });
        builder
            .addCase(refetchAllPagesThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(refetchAllPagesThunk.fulfilled, () => {
                //
            })
            .addCase(refetchAllPagesThunk.rejected, () => {
                //
            });
        builder
            .addCase(previewRenderFormThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(previewRenderFormThunk.fulfilled, (state, action) => {
                const { html, template_id } = action.payload as {
                    html: string;
                    template_id: number;
                };
                state.formHTML = html;
                state.formFields.formTemplateId = template_id;
            })
            .addCase(previewRenderFormThunk.rejected, (state) => {
                state.formHTML = '';
            });
        builder
            .addCase(fetchFormTemplatesThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(fetchFormTemplatesThunk.fulfilled, (state, action) => {
                state.formTemplates = action.payload as FormTemplatesObject[];
            })
            .addCase(fetchFormTemplatesThunk.rejected, (state) => {
                state.formTemplates = [];
            });
        builder
            .addCase(connectExternalFormThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(connectExternalFormThunk.fulfilled, () => {
                //
            })
            .addCase(connectExternalFormThunk.rejected, () => {
                //
            });
    }
});

// Exporting actions for updating app state
export const {
    settingFormManualMapping,
    setFilterForForms,
    settingFormSettingsFlag,
    settingSelectedFormFlag,
    settingSelectedFormType,
    reorderFormFields,
    settingFormFieldObject,
    duplicateFormField,
    addingFormField,
    editFormField,
    removeFormField,
    settingSelectedFormField,
    editFormTitle,
    settingSelectedForm,
    resettingFormData,
    resettingFormLoader,
    settingCreateFormFlag,
    settingPreviewFlag
} = formSlice.actions;

// Exporting the reducer to be used in the store
export default formSlice.reducer;
